import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

import classNames from 'classnames';

import { formatDateLong } from 'Helpers/dateFormat';

import useDropdown from 'Hooks/useDropdown';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';

const TARGET_MENU_ID = 'project-plan-critical-path-target-menu';

const criticalLabel = (count) => (count === 1 ? $t('1 activity · 0 days float') : $t('{{count}} activities · 0 days float', { count }));

const dateConflictsLabel = (count) => (count === 1 ? $t('1 date conflict') : $t('{{count}} date conflicts', { count }));

/** "Test event (1 Mar 2027)", or the description alone when the milestone has no date. */
const milestoneLabel = (milestone) => (milestone.date ? `${milestone.description} (${formatDateLong(milestone.date)})` : milestone.description);

const STAT_CLASS_NAME = 'tw-border-s tw-border-red-25 tw-ps-2.5';

/**
 * "Path to [target ▾] | N activities · 0 days float | N date conflicts" on the row-view line while
 * the Critical path switch is on. The counts cover the plan on screen and ignore every filter.
 */
const CriticalPathPill = ({ schedule, failed = false, onChangeTarget }) => {
    const { triggerRef, dropdownRef, isOpen, close, toggle } = useDropdown();

    // The menu is portalled to the end of <body>, so focus moves into it on open and back to the
    // trigger when Escape closes it.
    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        dropdownRef.current?.querySelector('[aria-checked="true"], button')?.focus();

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                close();
                triggerRef.current?.querySelector('button')?.focus();
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, close, dropdownRef, triggerRef]);

    if (failed) {
        return (
            <span
                className="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-grey tw-bg-white tw-px-3.5 tw-py-1.5 tw-text-0.5sm tw-text-charcoal-50"
                role="status"
            >
                {$t("Couldn't load critical path")}
            </span>
        );
    }

    if (!schedule) {
        return null;
    }

    const keyMilestones = schedule.key_milestones || [];

    if (!keyMilestones.length) {
        return (
            <span className="tw-inline-flex tw-items-center tw-rounded-full tw-border tw-border-grey tw-bg-white tw-px-3.5 tw-py-1.5 tw-text-0.5sm tw-text-charcoal-50">
                {$t('No key milestones')}
            </span>
        );
    }

    const target = keyMilestones.find((milestone) => milestone.id === schedule.target_id) || keyMilestones[0];
    const dateConflicts = schedule.counts?.date_conflicts ?? 0;

    return (
        <span
            ref={triggerRef}
            className="tw-inline-flex tw-items-center tw-gap-x-2.5 tw-rounded-full tw-border tw-border-red-25 tw-bg-red-10 tw-py-1.5 tw-ps-3.5 tw-pe-2 tw-text-0.5sm tw-text-charcoal"
        >
            <span className="tw-font-medium">{$t('Path to')}</span>
            <button
                type="button"
                onClick={toggle}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                aria-controls={isOpen ? TARGET_MENU_ID : undefined}
                aria-label={$t('Change the critical path target, currently {{target}}', { target: milestoneLabel(target) })}
                className="tw-inline-flex tw-items-center tw-gap-x-1.5 tw-rounded-full tw-border tw-border-red-25 tw-bg-white tw-px-2 tw-py-0.5 tw-font-medium tw-text-charcoal"
            >
                <span className="tw-max-w-[18rem] tw-truncate">{milestoneLabel(target)}</span>
                <ChevronDownIcon className="tw-w-2 tw-h-2" aria-hidden="true" />
            </button>
            <span className={classNames(STAT_CLASS_NAME, 'tw-text-charcoal-75')}>{criticalLabel(schedule.counts?.critical ?? 0)}</span>
            <span className={classNames(STAT_CLASS_NAME, dateConflicts ? 'tw-text-amber-700' : 'tw-text-dark-green')}>
                {dateConflicts ? dateConflictsLabel(dateConflicts) : $t('No date conflicts')}
            </span>
            {isOpen &&
                createPortal(
                    <div
                        ref={dropdownRef}
                        id={TARGET_MENU_ID}
                        role="menu"
                        aria-label={$t('Key milestones')}
                        className="tw-min-w-[13rem] tw-max-h-80 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-grey tw-bg-white tw-p-1.5 tw-text-start tw-shadow-primary"
                    >
                        {keyMilestones.map((milestone) => {
                            const selected = milestone.id === target.id;

                            return (
                                <button
                                    key={milestone.id}
                                    type="button"
                                    role="menuitemradio"
                                    aria-checked={selected}
                                    onClick={() => {
                                        close();
                                        if (!selected) {
                                            onChangeTarget(milestone.id);
                                        }
                                    }}
                                    className={classNames(
                                        'tw-flex tw-w-full tw-items-center tw-rounded tw-px-3 tw-py-1.5 tw-text-xs hover:tw-bg-background',
                                        selected ? 'tw-font-semibold tw-text-red' : 'tw-font-medium tw-text-charcoal',
                                    )}
                                >
                                    <span className="tw-truncate">{milestoneLabel(milestone)}</span>
                                </button>
                            );
                        })}
                    </div>,
                    document.body,
                )}
        </span>
    );
};

export default CriticalPathPill;
